<script lang="ts">
  import { store } from '../lib/store.svelte.ts';
  import { api } from '../lib/api.ts';

  /**
   * Hard gate. Nothing in the application is reachable without an identity — no catalogue,
   * no rounds, no reports.
   *
   * The identity may come from the local store rather than a live check, which is what lets
   * someone who has already signed in open the app in a dead zone. But it must exist: there
   * is no anonymous path in.
   */
  let authMode = $state<'dev' | 'oidc' | null>(null);
  let reachable = $state<boolean | null>(null);
  let devName = $state('');
  let busy = $state(false);
  let error = $state<string | null>(null);

  $effect(() => {
    void (async () => {
      try {
        const res = await fetch('/auth/mode', { credentials: 'include' });
        if (!res.ok) throw new Error(String(res.status));
        authMode = (await res.json()).mode;
        reachable = true;
      } catch {
        reachable = false;
      }
    })();
  });

  async function devLogin() {
    busy = true;
    error = null;
    try {
      await store.setIdentity(await api.devLogin(devName.trim() || 'local'));
      await store.sync();
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      busy = false;
    }
  }
</script>

<div class="login-wrap">
  <div class="login-card">
    <div class="login-mark" aria-hidden="true">
      <svg viewBox="0 0 12 12" width="52" height="52" aria-hidden="true">
        <rect width="12" height="12" rx="2.2" fill="var(--primary)" />
        {#each [[1, 1], [8, 1], [1, 8]] as [ox, oy] (`${ox}-${oy}`)}
          <rect x={ox} y={oy} width="3" height="3" fill="none" stroke="#ffffff" stroke-width="1" />
        {/each}
        {#each [[5, 2], [6, 3], [5, 4], [7, 5], [5, 6], [6, 6], [9, 6], [6, 10], [8, 10]] as [x, y] (`${x}-${y}`)}
          <rect x={x} y={y} width="1" height="1" fill="var(--accent)" opacity="0.95" />
        {/each}
      </svg>
    </div>

    <h1>Chariot</h1>
    <p class="small subtle">Suivi de la flotte de chariots par QR code</p>

    {#if reachable === null}
      <p class="small subtle" style="margin-top:1rem"><span class="spinner"></span> Vérification…</p>
    {:else if reachable === false}
      <div class="banner warn">
        Serveur injoignable, et aucune session enregistrée sur cet appareil.
        <br />
        Une première connexion doit se faire en ligne. Ensuite, l'application s'ouvrira et
        fonctionnera hors connexion.
      </div>
      <div class="login-actions"><button class="block" onclick={() => location.reload()}>Réessayer</button></div>
    {:else if authMode === 'oidc'}
      <div class="login-actions">
        <a class="btn primary block big" href="/auth/login">Se connecter</a>
      </div>
      <p class="login-foot">Authentification par le SSO DSI</p>
    {:else}
      <div class="banner warn">
        Mode développement : identité locale, sans SSO.
      </div>
      <label class="field"><span>Nom</span><input bind:value={devName} placeholder="local" /></label>
      <div class="login-actions">
        <button class="primary block big" onclick={devLogin} disabled={busy}>
          {#if busy}<span class="spinner"></span>{/if} Se connecter
        </button>
      </div>
    {/if}

    {#if error}<div class="banner danger" style="margin-top:.8rem">{error}</div>{/if}
  </div>
</div>

<style>
  .login-wrap {
    min-height: 100dvh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    background: var(--canvas);
  }
  .login-card {
    width: 100%;
    max-width: 380px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--r-card);
    box-shadow: var(--shadow-sm);
    padding: 2rem 1.5rem 1.5rem;
    text-align: center;
  }
  .login-mark { margin-bottom: 1rem; line-height: 0; }
  .login-mark :global(svg) { border-radius: 10px; }
  .login-card h1 { font-size: 1.25rem; margin-bottom: .15rem; }
  .login-card :global(.field) { text-align: left; }
  .login-actions { margin-top: 1.25rem; }
  .login-foot {
    margin: 1.25rem -1.5rem -1.5rem;
    padding: .75rem 1.5rem;
    border-top: 1px solid var(--divider);
    background: var(--canvas);
    border-radius: 0 0 var(--r-card) var(--r-card);
    font-size: var(--text-label);
    color: var(--fg-subtle);
  }
</style>
